/**
 * The opened folder: listings for the tree, the flat list behind Articles and
 * Open Quickly, and the file operations the sidebar and File menu need.
 * Ported from the desktop's `main/workspace.ts`, `main/fileops.ts`,
 * `main/replace.ts` and the workspace IPC.
 *
 * A folder is either opened with read/write access (`ws:` paths, File System
 * Access) or read as a snapshot (`snap:` paths, every other browser), which can
 * be browsed, searched and linked but not changed.
 *
 * Everything is confined to the folder by construction: a directory handle
 * cannot reach above itself, and paths that climb out with `..` resolve to
 * nothing (see `insideFolder`).
 */
import type { DirEntry, FileProperties, MarkdownFile, PreviewFile, ReplaceResult } from './types'
import {
  directoryFor,
  fileFor,
  fileHandleFor,
  getOpenFolder,
  hasFileSystemAccess,
  handleRecord,
  pathForDirectoryHandle,
  pathForFileHandle,
  PlatformError,
  registerSnapshotFolder,
  setOpenFolder,
  snapshotFolder,
  toPlatformError,
} from './handles'
import { baseName, displayPath, formatPath, insideFolder, parsePath, validName } from './paths'
import { getSettings, onSettingsChanged, patchSettings } from './settings'
import { decodeText, encodeText } from './textfile'
import { backup, clearJournal, moveHistoryUnder } from './recovery'
import { observeFolder, untrack, watchFolder } from './watch'
import { pickWithInput } from './files'
import { ask } from './dialogs'
import { compileSearch, previewText, replaceAllIn, type ReplaceSpec } from '../shared/text-search'

export const MARKDOWN_EXTENSIONS = ['.md', '.markdown', '.mdown', '.mkd', '.mdx']

/** Never worth walking, and walking them is how a scan turns into a hang. */
const ALWAYS_SKIP = new Set([
  '.git',
  'node_modules',
  '.svn',
  '.hg',
  '$RECYCLE.BIN',
  'System Volume Information',
])

export function isMarkdown(name: string): boolean {
  const lower = name.toLowerCase()
  return MARKDOWN_EXTENSIONS.some((e) => lower.endsWith(e))
}

function skip(name: string, ignores?: ReadonlySet<string>): boolean {
  return ALWAYS_SKIP.has(name) || name.startsWith('.') || (ignores?.has(name) ?? false)
}

const IMAGE = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i

function byFolderThenName(a: DirEntry, b: DirEntry): number {
  if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
}

/**
 * Minimal .gitignore support: literal names and simple directory patterns.
 * Deliberately not git's matching rules: the goal is only to avoid walking
 * build output in a notes folder that happens to be a repository.
 */
export function parseIgnores(text: string): Set<string> {
  const out = new Set<string>()
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim()
    if (!line || line.startsWith('#') || line.startsWith('!')) continue
    if (line.includes('*') || line.includes('?')) continue
    out.add(line.replace(/^\/+/, '').replace(/\/+$/, ''))
  }
  return out
}

async function loadIgnores(root: string): Promise<Set<string>> {
  try {
    const file = await fileFor(`${root}/.gitignore`, { ask: false })
    return parseIgnores(await file.text())
  } catch {
    return new Set()
  }
}

/** The root of the folder a path is in. */
function rootOf(path: string): string {
  const p = parsePath(path)
  if (!p || (p.scheme !== 'ws' && p.scheme !== 'snap') || p.segments.length === 0) {
    throw new PlatformError('ENOENT', 'That is not in an open folder.')
  }
  return formatPath(p.scheme, p.id, [p.segments[0]])
}

// --- listing ----------------------------------------------------------------

/** One level only; the tree asks for children when a folder is expanded. */
export async function readDir(dir: string): Promise<DirEntry[]> {
  const p = parsePath(dir)
  if (!p) throw new PlatformError('ENOENT', `${dir} is not a folder this page can read.`)
  const ignores = await loadIgnores(rootOf(dir))
  const out: DirEntry[] = []

  if (p.scheme === 'snap') {
    const folder = snapshotFolder(p.id)
    const inside = insideFolder(p)
    if (!folder || !inside) throw new PlatformError('ENOENT', 'That folder is no longer open.')
    const prefix = inside.length ? `${inside.join('/')}/` : ''
    const seen = new Set<string>()
    for (const rel of folder.files.keys()) {
      if (!rel.startsWith(prefix)) continue
      const [first, ...rest] = rel.slice(prefix.length).split('/')
      if (!first || seen.has(first) || skip(first, ignores)) continue
      const isDirectory = rest.length > 0
      if (!isDirectory && !isMarkdown(first)) continue
      seen.add(first)
      out.push({ name: first, path: `${dir}/${first}`, isDirectory, isMarkdown: !isDirectory })
    }
    return out.sort(byFolderThenName)
  }

  const handle = await directoryFor(dir, 'read')
  try {
    for await (const [name, entry] of handle.entries()) {
      if (skip(name, ignores)) continue
      const isDirectory = entry.kind === 'directory'
      if (!isDirectory && !isMarkdown(name)) continue
      out.push({ name, path: `${dir}/${name}`, isDirectory, isMarkdown: !isDirectory })
    }
  } catch (err) {
    throw toPlatformError(err, handle.name)
  }
  return out.sort(byFolderThenName)
}

/**
 * Every markdown file under the root, flat. Bounded by depth and count so a
 * folder opened by mistake — a whole drive, say — cannot walk forever.
 */
export async function allMarkdown(
  root: string,
  opts: { maxDepth?: number; maxFiles?: number; ask?: boolean } = {}
): Promise<MarkdownFile[]> {
  const maxDepth = opts.maxDepth ?? 12
  const maxFiles = opts.maxFiles ?? 20_000
  const p = parsePath(root)
  if (!p) return []
  const ignores = await loadIgnores(rootOf(root))
  const rootPath = rootOf(root)
  const out: MarkdownFile[] = []
  const relative = (path: string): string => path.slice(rootPath.length + 1)

  if (p.scheme === 'snap') {
    const folder = snapshotFolder(p.id)
    if (!folder) return []
    for (const [rel, file] of folder.files) {
      if (out.length >= maxFiles) break
      const parts = rel.split('/')
      if (parts.length - 1 > maxDepth || parts.some((s) => skip(s, ignores))) continue
      if (!isMarkdown(parts[parts.length - 1])) continue
      const path = `${rootPath}/${rel}`
      out.push({
        path,
        name: file.name,
        relativePath: rel,
        mtimeMs: file.lastModified,
        size: file.size,
      })
    }
  } else {
    const walk = async (
      dir: FileSystemDirectoryHandle,
      path: string,
      depth: number
    ): Promise<void> => {
      if (depth > maxDepth || out.length >= maxFiles) return
      try {
        for await (const [name, entry] of dir.entries()) {
          if (out.length >= maxFiles) return
          if (skip(name, ignores)) continue
          const full = `${path}/${name}`
          if (entry.kind === 'directory') {
            await walk(entry as FileSystemDirectoryHandle, full, depth + 1)
          } else if (isMarkdown(name)) {
            try {
              const file = await (entry as FileSystemFileHandle).getFile()
              out.push({
                path: full,
                name,
                relativePath: relative(full),
                mtimeMs: file.lastModified,
                size: file.size,
              })
            } catch {
              // Vanished between the listing and the read.
            }
          }
        }
      } catch {
        // An unreadable folder is skipped rather than aborting the whole scan.
      }
    }
    await walk(await directoryFor(root, 'read', { ask: opts.ask }), root, 0)
  }
  return out.sort((a, b) =>
    a.relativePath.localeCompare(b.relativePath, undefined, { sensitivity: 'base' })
  )
}

// --- opening a folder -----------------------------------------------------------

/** Every file under a folder read through <input webkitdirectory>, by its path inside. */
function snapshotFiles(files: File[]): {
  name: string
  files: Array<{ path: string; file: File }>
} {
  const first = (files[0] as File & { webkitRelativePath?: string })?.webkitRelativePath ?? ''
  const name = first.split('/')[0] || 'Folder'
  const kept: Array<{ path: string; file: File }> = []
  for (const file of files) {
    const rel = ((file as File & { webkitRelativePath?: string }).webkitRelativePath ?? file.name)
      .split('/')
      .slice(1)
      .join('/')
    const leaf = rel.split('/').pop() ?? ''
    // Only what the app can use: documents, the images they show, and .gitignore.
    if (isMarkdown(leaf) || IMAGE.test(leaf) || leaf === '.gitignore')
      kept.push({ path: rel, file })
  }
  return { name, files: kept }
}

/** Starts following the open folder: for the tree, wiki links and image links. */
async function follow(root: string | null): Promise<void> {
  setOpenFolder(root)
  if (!root) {
    watchFolder(null)
    return
  }
  watchFolder(async () => {
    const files = await allMarkdown(root, { ask: false })
    return new Map(files.map((f) => [f.path, f.mtimeMs]))
  })
  const p = parsePath(root)
  if (p?.scheme === 'ws') {
    const rec = await handleRecord(p.id)
    if (rec?.kind === 'directory') observeFolder(rec.handle as FileSystemDirectoryHandle)
  }
}

/** File ▸ Open Folder. Resolves to the folder's path, or null when cancelled. */
export async function openFolderDialog(): Promise<string | null> {
  let root: string
  if (hasFileSystemAccess) {
    try {
      const handle = await window.showDirectoryPicker({ mode: 'readwrite' })
      root = await pathForDirectoryHandle(handle)
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return null
      throw toPlatformError(err, 'the folder')
    }
  } else {
    const files = await pickWithInput({ directory: true })
    if (!files || files.length === 0) return null
    const snap = snapshotFiles(files)
    root = registerSnapshotFolder(snap.name, snap.files)
  }
  return openFolderAt(root)
}

/** Opens a folder already granted: from a drop, or a reconnection. */
export async function openFolderAt(root: string): Promise<string> {
  // The sidebar is hidden until there is a folder to show, then shows it.
  patchSettings({ workspace: root, sidebar: { visible: true, panel: 'files' } })
  await follow(root)
  return root
}

/** Opens a dropped folder handle, or a dropped folder's files read without one. */
export async function openDroppedFolder(
  dropped: FileSystemDirectoryHandle | { name: string; files: Array<{ path: string; file: File }> }
): Promise<string> {
  const root =
    'kind' in dropped
      ? await pathForDirectoryHandle(dropped)
      : registerSnapshotFolder(
          dropped.name,
          dropped.files.filter((f) => {
            const leaf = f.path.split('/').pop() ?? ''
            return isMarkdown(leaf) || IMAGE.test(leaf) || leaf === '.gitignore'
          })
        )
  return openFolderAt(root)
}

export async function setWorkspace(root: string | null): Promise<string | null> {
  patchSettings({ workspace: root })
  await follow(root)
  return root
}

export function initWorkspace(): void {
  void follow(getSettings().workspace)
  // Another tab opening a folder moves this one to it, as on the desktop.
  onSettingsChanged((s) => {
    if ((s.workspace ?? null) !== getOpenFolder()) void follow(s.workspace ?? null)
  })
}

// --- file operations -------------------------------------------------------------

function parentOf(path: string): string {
  return path.slice(0, path.lastIndexOf('/'))
}

export async function properties(path: string): Promise<FileProperties> {
  const file = await fileFor(path)
  return {
    path: displayPath(path),
    name: baseName(path),
    size: file.size,
    // A browser reports no creation time.
    createdMs: file.lastModified,
    modifiedMs: file.lastModified,
  }
}

function writableFolder(path: string): void {
  if (parsePath(path)?.scheme !== 'ws') {
    throw new PlatformError(
      'EROFS',
      'This folder was opened read-only, because this browser cannot change files.'
    )
  }
}

async function exists(dir: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await dir.getFileHandle(name)
    return true
  } catch {
    try {
      await dir.getDirectoryHandle(name)
      return true
    } catch {
      return false
    }
  }
}

function checkName(name: string): void {
  const problem = validName(name)
  if (problem) throw new PlatformError('EINVAL', problem)
}

/** Makes an empty markdown file in `dir`, adding .md; never over an existing one. */
export async function createFile(dir: string, name: string): Promise<string> {
  writableFolder(dir)
  checkName(name)
  const fileName = isMarkdown(name) ? name : `${name}.md`
  const folder = await directoryFor(dir, 'readwrite')
  if (await exists(folder, fileName))
    throw new PlatformError('EEXIST', `${fileName} is already there.`)
  try {
    await folder.getFileHandle(fileName, { create: true })
  } catch (err) {
    throw toPlatformError(err, fileName)
  }
  return `${dir}/${fileName}`
}

export async function createFolder(dir: string, name: string): Promise<string> {
  writableFolder(dir)
  checkName(name)
  const folder = await directoryFor(dir, 'readwrite')
  if (await exists(folder, name)) throw new PlatformError('EEXIST', `${name} is already there.`)
  try {
    await folder.getDirectoryHandle(name, { create: true })
  } catch (err) {
    throw toPlatformError(err, name)
  }
  return `${dir}/${name}`
}

type Movable = FileSystemHandle & {
  move?: (parentOrName: FileSystemDirectoryHandle | string, name?: string) => Promise<void>
}

/** Copies a file's bytes to a new name in the same folder, then removes the old one. */
async function copyThenRemove(
  folder: FileSystemDirectoryHandle,
  from: string,
  to: string
): Promise<void> {
  const source = await folder.getFileHandle(from)
  const bytes = await (await source.getFile()).arrayBuffer()
  const target = await folder.getFileHandle(to, { create: true })
  const w = await target.createWritable()
  await w.write(bytes)
  await w.close()
  await folder.removeEntry(from)
}

/**
 * Renames a file or folder in place; returns its new path. A file keeps its
 * extension when the new name has none, and a name that is taken is refused,
 * except by the same file with its case changed.
 */
export async function rename(path: string, name: string): Promise<string> {
  writableFolder(path)
  checkName(name)
  const p = parsePath(path)!
  const inside = insideFolder(p)
  if (!inside || inside.length === 0)
    throw new PlatformError('EINVAL', 'That is the open folder itself.')
  const oldName = inside[inside.length - 1]
  const parent = await directoryFor(parentOf(path), 'readwrite')
  let handle: FileSystemHandle
  try {
    handle = await parent.getFileHandle(oldName).catch(() => parent.getDirectoryHandle(oldName))
  } catch (err) {
    throw toPlatformError(err, oldName)
  }
  const isDir = handle.kind === 'directory'
  const ext = oldName.includes('.') ? oldName.slice(oldName.lastIndexOf('.')) : ''
  const newName = !isDir && !name.includes('.') ? `${name}${ext}` : name
  if (newName === oldName) return path
  const sameFile = newName.toLowerCase() === oldName.toLowerCase()
  if (!sameFile && (await exists(parent, newName))) {
    throw new PlatformError('EEXIST', `${newName} is already there.`)
  }
  try {
    const movable = handle as Movable
    if (typeof movable.move === 'function') await movable.move(newName)
    else if (!isDir) await copyThenRemove(parent, oldName, newName)
    else throw new PlatformError('ENOTSUP', 'This browser cannot rename folders.')
  } catch (err) {
    throw toPlatformError(err, oldName)
  }
  const to = `${parentOf(path)}/${newName}`
  untrack(path)
  await moveHistoryUnder(path, to)
  return to
}

/** The markdown files in a folder, for keeping copies before it is deleted. */
async function backupFolder(dir: FileSystemDirectoryHandle, path: string): Promise<void> {
  for await (const [name, entry] of dir.entries()) {
    if (entry.kind === 'directory')
      await backupFolder(entry as FileSystemDirectoryHandle, `${path}/${name}`)
    else if (isMarkdown(name)) {
      const bytes = new Uint8Array(
        await (await (entry as FileSystemFileHandle).getFile()).arrayBuffer()
      )
      await backup(`${path}/${name}`, bytes)
    }
  }
}

/**
 * Deletes a file or folder in the open folder, after asking. A browser cannot
 * use the Recycle Bin, so the question says the deletion is permanent; the
 * markdown it held is kept in Data Recovery first all the same.
 */
export async function trash(path: string, opts: { confirm?: boolean } = {}): Promise<boolean> {
  writableFolder(path)
  const p = parsePath(path)!
  const inside = insideFolder(p)
  if (!inside || inside.length === 0)
    throw new PlatformError('EINVAL', 'That is the open folder itself.')
  const name = inside[inside.length - 1]
  const parent = await directoryFor(parentOf(path), 'readwrite')
  const dir = await parent.getDirectoryHandle(name).catch(() => null)

  if (opts.confirm !== false) {
    const choice = await ask({
      kind: 'warning',
      message: `Delete “${name}”?`,
      detail: dir
        ? 'The folder and everything in it will be deleted. Browsers delete files permanently: there is no Recycle Bin to restore them from.'
        : 'Browsers delete files permanently: there is no Recycle Bin to restore it from.',
      buttons: ['Delete', 'Cancel'],
      defaultId: 1,
      cancelId: 1,
    })
    if (choice !== 0) return false
  }

  try {
    if (dir) await backupFolder(dir, path)
    else {
      const bytes = new Uint8Array(
        await (await (await parent.getFileHandle(name)).getFile()).arrayBuffer()
      )
      await backup(path, bytes)
    }
    await parent.removeEntry(name, { recursive: true })
  } catch (err) {
    throw toPlatformError(err, name)
  }
  untrack(path)
  await clearJournal(path)
  return true
}

/** File ▸ Delete, for the document in front: inside the open folder, or a file of its own. */
export async function deleteDocument(path: string): Promise<boolean> {
  const p = parsePath(path)
  if (p?.scheme === 'ws') return trash(path)
  if (p?.scheme !== 'fh') {
    throw new PlatformError('EROFS', 'Only a file opened from disk can be deleted from here.')
  }
  const choice = await ask({
    kind: 'warning',
    message: `Delete “${baseName(path)}”?`,
    detail: 'Browsers delete files permanently: there is no Recycle Bin to restore it from.',
    buttons: ['Delete', 'Cancel'],
    defaultId: 1,
    cancelId: 1,
  })
  if (choice !== 0) return false
  const handle = (await fileHandleFor(path, 'readwrite')) as FileSystemFileHandle & {
    remove?: () => Promise<void>
  }
  if (typeof handle.remove !== 'function') {
    throw new PlatformError(
      'ENOTSUP',
      'This browser can only delete files inside a folder opened with File ▸ Open Folder.'
    )
  }
  await backup(path, new Uint8Array(await (await handle.getFile()).arrayBuffer()))
  await handle.remove()
  untrack(path)
  await clearJournal(path)
  return true
}

/** File ▸ Move To: a copy where the user chooses, then the original removed. */
export async function moveDocument(path: string): Promise<string | null> {
  if (!hasFileSystemAccess) throw new PlatformError('ENOTSUP', 'This browser cannot move files.')
  const source = await fileHandleFor(path, 'readwrite')
  let target: FileSystemFileHandle
  try {
    target = await window.showSaveFilePicker({ suggestedName: source.name })
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return null
    throw toPlatformError(err, source.name)
  }
  if (await target.isSameEntry(source)) return null
  const bytes = await (await source.getFile()).arrayBuffer()
  const w = await target.createWritable()
  await w.write(bytes)
  await w.close()
  const to = await pathForFileHandle(target)
  const p = parsePath(path)!
  try {
    if (p.scheme === 'ws') {
      const parent = await directoryFor(parentOf(path), 'readwrite')
      await parent.removeEntry(source.name)
    } else {
      const removable = source as FileSystemFileHandle & { remove?: () => Promise<void> }
      if (typeof removable.remove === 'function') await removable.remove()
    }
  } catch {
    // The copy is made; the original stays where it was. Said by the caller
    // only if it matters; the document now follows the copy.
  }
  untrack(path)
  await clearJournal(path)
  await moveHistoryUnder(path, to)
  return to
}

// --- replace across the folder ---------------------------------------------------

const MAX_FILE_BYTES = 1024 * 1024

function compile(spec: ReplaceSpec): RegExp {
  const c = compileSearch(spec)
  if (!c.ok) throw new Error(c.error)
  return c.re
}

/** The files that would change, leaving out those in `open` (the page has those). */
export async function previewReplace(spec: ReplaceSpec, open: string[]): Promise<PreviewFile[]> {
  const root = getOpenFolder()
  if (!root) throw new Error('No folder is open.')
  const re = compile(spec)
  const skipped = new Set(open.map((p) => p.toLowerCase()))
  const out: PreviewFile[] = []
  for (const file of await allMarkdown(root)) {
    if (skipped.has(file.path.toLowerCase()) || file.size > MAX_FILE_BYTES) continue
    const text = decodeText(new Uint8Array(await (await fileFor(file.path)).arrayBuffer())).content
    const { count, samples } = previewText(text, re, spec)
    if (count > 0)
      out.push({
        path: file.path,
        relativePath: file.relativePath,
        mtimeMs: file.mtimeMs,
        count,
        samples,
      })
  }
  return out
}

/** Replaces in the files chosen from the preview; a file changed since is skipped. */
export async function applyReplace(
  spec: ReplaceSpec,
  files: Array<{ path: string; mtimeMs: number }>
): Promise<ReplaceResult> {
  const root = getOpenFolder()
  if (!root) throw new Error('No folder is open.')
  writableFolder(root)
  const re = compile(spec)
  const done: ReplaceResult['done'] = []
  const skippedFiles: ReplaceResult['skipped'] = []
  for (const { path, mtimeMs } of files) {
    try {
      if (!path.toLowerCase().startsWith(`${root.toLowerCase()}/`)) {
        throw new Error('it is not inside the open folder')
      }
      const handle = await fileHandleFor(path, 'readwrite')
      const file = await handle.getFile()
      if (file.lastModified !== mtimeMs) {
        skippedFiles.push({ path, reason: 'it changed since the preview' })
        continue
      }
      const bytes = new Uint8Array(await file.arrayBuffer())
      const text = decodeText(bytes)
      const result = replaceAllIn(text.content, re, spec.replacement, spec.regexp === true)
      if (result.count === 0) continue
      await backup(path, bytes)
      const w = await handle.createWritable()
      await w.write(encodeText(result.text, text) as BufferSource)
      await w.close()
      done.push({ path, count: result.count })
    } catch (err) {
      skippedFiles.push({ path, reason: String((err as Error).message ?? err) })
    }
  }
  return { done, skipped: skippedFiles }
}
